<script setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import Navbar from "./Navbar.vue";
import { UnsupportedBrowserReasonType } from "../utils/browser.js";
import { DeviceInfo } from "../utils/deviceInfo.js";
import defaultQRCodeImage from "../assets/qr.png";
import Footer from "@/components/Footer.vue";

const props = defineProps({
  deviceInfo: DeviceInfo,
  unsupportedBrowserReason: UnsupportedBrowserReasonType
});
const { locale } = useI18n();
const version = __APP_VERSION__;

const isUnsupportedVersion = props.unsupportedBrowserReason === UnsupportedBrowserReasonType.VERSION;
const isAndroid = props.deviceInfo.isAndroid();
const isIos = props.deviceInfo.isIos();
const isMobileOrTablet = props.deviceInfo.isMobileOrTablet();
const isWechat = props.deviceInfo.isWechat();
const isChromiumBased = props.deviceInfo.isChromiumBased();
const browserName = props.deviceInfo.getBrowser();

const isChinese = computed(() => locale.value === "zh");

const downloadOrUpgradeLink = computed(() => {
  const chromeLink = "https://play.google.com/store/apps/details?id=com.android.chrome";
  const browserLink = `https://play.google.com/store/search?q=${encodeURIComponent(browserName)}&c=apps`;
  const safariLink = "https://apps.apple.com/us/app/safari/id1146562112";
  if (isIos) {
    return safariLink;
  }
  return isChromiumBased && isUnsupportedVersion ? browserLink : chromeLink;
});

const PHOTO_AUTH_URL = `${window.location.origin}/${import.meta.env.VITE_PHOTO_AUTH_PREFIX}${window.location.search}`;

const handleQrCodeError = (event) => {
  event.target.src = defaultQRCodeImage;
};
const qrCodeUrl = computed(() => `https://qr.svc.scantrust.com/api/qr/single/?content=${encodeURIComponent(window.location.href)}&image_format=svg&error_correction=l`);
</script>

<template>
<Navbar />
<div class="text_all px-7 py-8">
  <!--- Upgrade Wechat or OS if Wechat --->
  <p v-if="isWechat" class="mb-10 text-center text-2xl leading-6 font-bold">
    {{ $t("unsupported.incompatible_phone") }}
  </p>
  <!--- non chrome based on android OR non-version related issue on iOS = incompatible--->
  <p
    v-else-if="isChinese || (isAndroid && (!isChromiumBased || !isUnsupportedVersion)) || (isIos && !isUnsupportedVersion)"
    class="mb-10 text-center text-2xl leading-6 font-bold"
  >
    {{ $t("unsupported.incompatible") }}
  </p>
  <!--- Upgrade chrome browser if chromium --->
  <p v-else-if="isAndroid && isChromiumBased && isUnsupportedVersion" class="mb-10 text-center text-2xl leading-6 font-bold">
    {{ $t("unsupported.upgrade_browser") }}
  </p>
  <!--- Incompatible iOS version--->
  <p v-else-if="isIos && isUnsupportedVersion" class="mb-10 text-center text-2xl leading-6 font-bold">
    {{ $t("unsupported.incompatible_ios") }}
  </p>
  <!--- if not on mobile -->
  <p v-else class="mt-10 mb-10 text-center text-2xl leading-6 font-bold md:mt-[120px]">
    {{ $t("unsupported.open_on_mobile") }}
  </p>

  <div v-if="isChinese" class="text_all text-center text-lg leading-6 font-normal tracking-[0.015em]">
    {{ $t("unsupported.china_unsupported") }}
  </div>
  <div v-else class="text_all text-center">
    <!--- Upgrade Wechat or OS if Wechat --->
    <div v-if="isAndroid && isWechat" class="text_all text-left text-lg leading-6 font-normal tracking-[0.015em]">
      {{ $t("unsupported.upgrade_wechat") }}
    </div>
    <!--- Upgrade chrome browser if chromium --->
    <div v-else-if="isAndroid && isChromiumBased && isUnsupportedVersion" class="text_all text-left text-lg leading-6 font-normal tracking-[0.015em]">
      {{ $t("unsupported.latest_browser") }}
      <br />
      {{ $t("unsupported.upgrade") }}
    </div>
    <!--- non chrome based on android = incompatible--->
    <div v-else-if="isAndroid && (!isChromiumBased || !isUnsupportedVersion)" class="text_all text-left text-lg leading-6 font-normal tracking-[0.015em]">
      {{ $t("unsupported.chrome_browser") }}
      <br />
      {{ $t("unsupported.download") }}
    </div>
    <!--- non-version related issue on iOS = incompatible--->
    <div v-else-if="isIos && !isUnsupportedVersion" class="text_all text-left text-lg leading-6 font-normal tracking-[0.015em]">
      {{ $t("unsupported.safari_browser") }}
    </div>
    <!--- Incompatible iOS version --->
    <div v-else-if="isIos && isUnsupportedVersion" class="text_all text-left text-lg leading-6 font-normal tracking-[0.015em]">
      {{ $t("unsupported.update_ios") }}
    </div>
    <!--- if not on mobile -->
    <div v-else class="text-center text-lg leading-6 font-normal tracking-[0.015em]">
      {{ $t("unsupported.use_mobile") }}
      <br />
      {{ $t("unsupported.scan_on_phone") }}
    </div>
  </div>
  <div class="text-center">
    <div v-if="isChinese" class="mb-48 inline-flex">
      <img class="mt-12 h-16 w-16" src="../assets/wechat.svg" alt="WeChat logo" />
    </div>
    <div v-else-if="isWechat"></div>
    <a v-else-if="isAndroid" class="mt-10 mb-48 inline-block" :href="downloadOrUpgradeLink">
      <img class="h-16 w-52" alt="" src="../assets/google_play.png" />
    </a>
    <a v-else-if="isIos && isUnsupportedVersion" class="mt-5 mb-48 inline-block md:max-w-[600px]">
      <img class="w-80 md:w-full" src="../assets/ios_update.png" alt="ios" />
    </a>
    <a v-else-if="isIos && !isUnsupportedVersion" class="mt-10 mb-48 inline-block" :href="downloadOrUpgradeLink">
      <img class="h-16 w-52" alt="app store" src="../assets/app_store.png" />
    </a>
    <div v-else class="inline-flex">
      <img class="mt-12" width="166" height="166" :src="qrCodeUrl" crossorigin="anonymous" alt="Scantrust QR Code" @error="handleQrCodeError" />
    </div>
  </div>
  <div v-if="isMobileOrTablet || isAndroid || isIos" class="mt-5">
    <p class="text-center text-base leading-[23px] font-bold tracking-[0.015em]">
      {{ $t("unsupported.try_app_auth") }}
    </p>
    <a class="block py-2 text-center text-base leading-[19px] font-normal text-(--secondary) underline" :href="PHOTO_AUTH_URL">
      {{ $t("btn_photo_auth") }}
    </a>
  </div>
</div>
<Footer :version="version" />
</template>

<style scoped></style>
